import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getAdminStats, getPendingNews, approveNews, rejectNews, getAdminAnalytics, getAdminUsersDetails, adminDeleteUser, getAdminReports, updateReportStatus, deleteReport, getFileUrl, triggerAutoNews, getAutoNewsStatus } from '../lib/api';
import { NewsCardAdmin } from '../components/NewsCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Loader2, Users, Newspaper, Clock, CheckCircle, Eye, TrendingUp, BarChart3, UserCheck, CreditCard, Trash2, Megaphone, MapPin, Image as ImageIcon, Video as VideoIcon, Archive, RefreshCw, Map as MapIcon, List, Download, Package, Sparkles, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import ReportsMap from '../components/ReportsMap';
import { watermarkImageBlob, isWatermarkableImage } from '../lib/watermark';

export default function AdminPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportsView, setReportsView] = useState('list'); // 'list' | 'map'
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('analytics');

  const fetch_ = useCallback(async () => {
    try {
      const [s, p, a, u, r] = await Promise.all([
        getAdminStats(), getPendingNews(), getAdminAnalytics(), getAdminUsersDetails(), getAdminReports()
      ]);
      setStats(s.data); setPending(p.data); setAnalytics(a.data); setUsersData(u.data);
      // New paginated shape: { reports, total, page, pages }
      setReports(Array.isArray(r.data) ? r.data : (r.data.reports || []));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (!user || user.role !== 'admin') { nav('/'); return; } fetch_(); }, [user, nav, fetch_]);

  // Poll for new reports every 30s and notify if unseen
  useEffect(() => {
    if (!user || user.role !== 'admin') return;
    const last = parseInt(localStorage.getItem('rp_last_seen_reports') || '0', 10);
    const poll = async () => {
      try {
        const { data } = await getAdminReports();
        const list = Array.isArray(data) ? data : (data.reports || []);
        setReports(list);
        const newest = list.length ? new Date(list[0].created_at).getTime() : 0;
        if (newest > last && last > 0 && tab !== 'reports') {
          toast.info('Nueva denuncia recibida');
        }
      } catch {}
    };
    const id = setInterval(poll, 30000);
    return () => clearInterval(id);
  }, [user, tab]);

  // When admin opens reports tab, mark as seen
  useEffect(() => {
    if (tab === 'reports' && reports.length > 0) {
      const newest = new Date(reports[0].created_at).getTime();
      localStorage.setItem('rp_last_seen_reports', String(newest));
    }
  }, [tab, reports]);

  const approve = async (id) => {
    try { await approveNews(id); toast.success('Aprobada'); setPending(p => p.filter(n => n.id !== id)); setStats(p => p ? { ...p, approved: p.approved + 1, pending: p.pending - 1 } : p); }
    catch { toast.error('Error'); }
  };
  const reject = async (id) => {
    try { await rejectNews(id); toast.success('Rechazada'); setPending(p => p.filter(n => n.id !== id)); setStats(p => p ? { ...p, rejected: p.rejected + 1, pending: p.pending - 1 } : p); }
    catch { toast.error('Error'); }
  };

  const removeUser = async (uid, uname) => {
    try {
      await adminDeleteUser(uid);
      toast.success(`Usuario "${uname}" eliminado`);
      setUsersData(list => list.filter(u => u.id !== uid));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  const setReportStatus = async (rid, status) => {
    try {
      await updateReportStatus(rid, status);
      setReports(list => list.map(r => r.id === rid ? { ...r, status } : r));
      if (selectedReport?.id === rid) setSelectedReport(s => ({ ...s, status }));
      toast.success('Estado actualizado');
    } catch { toast.error('Error al actualizar'); }
  };

  const removeReport = async (rid) => {
    try {
      await deleteReport(rid);
      setReports(list => list.filter(r => r.id !== rid));
      if (selectedReport?.id === rid) setSelectedReport(null);
      toast.success('Denuncia eliminada');
    } catch { toast.error('Error al eliminar'); }
  };

  // Download helpers
  const [downloading, setDownloading] = useState(false);
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  const [autoNewsStatus, setAutoNewsStatus] = useState(null);
  const [autoNewsRunning, setAutoNewsRunning] = useState(false);

  const loadAutoNews = useCallback(async () => {
    try { const { data } = await getAutoNewsStatus(); setAutoNewsStatus(data); } catch {}
  }, []);

  useEffect(() => { if (user?.role === 'admin') loadAutoNews(); }, [user, loadAutoNews]);

  const runAutoNewsNow = async () => {
    if (autoNewsRunning) return;
    setAutoNewsRunning(true);
    try {
      const { data } = await triggerAutoNews();
      if (data.note === 'already_ran_today') toast.info('Ya se generaron notas hoy. Intenta mañana.');
      else if (data.inserted > 0) toast.success(`${data.inserted} notas generadas. Las imágenes se procesan en segundo plano.`);
      else toast.warning(`Sin nuevas notas (errores: ${data.errors || 0}).`);
      await loadAutoNews();
      await fetch_();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar');
    } finally { setAutoNewsRunning(false); }
  };

  const downloadOne = async (path, refCode = '') => {
    try {
      const url = getFileUrl(path);
      const res = await fetch(url, { credentials: 'include' });
      let blob = await res.blob();
      let filename = path.split('/').pop();
      if (watermarkEnabled && isWatermarkableImage(blob)) {
        blob = await watermarkImageBlob(blob, refCode);
        // Force .png since we re-exported
        filename = filename.replace(/\.[^.]+$/, '') + '-wm.png';
      }
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
    } catch {
      toast.error('Error al descargar');
    }
  };

  const downloadAll = async (report) => {
    const allPaths = [...(report.images || []), ...(report.videos || [])];
    if (allPaths.length === 0) { toast.error('Sin archivos para descargar'); return; }
    setDownloading(true);
    try {
      const [{ default: JSZip }, { saveAs }] = await Promise.all([
        import('jszip'),
        import('file-saver'),
      ]);
      const zip = new JSZip();
      const folder = zip.folder(`denuncia-${report.ref_code}`);
      await Promise.all(allPaths.map(async (p) => {
        try {
          const res = await fetch(getFileUrl(p), { credentials: 'include' });
          let blob = await res.blob();
          let name = p.split('/').pop();
          if (watermarkEnabled && isWatermarkableImage(blob)) {
            blob = await watermarkImageBlob(blob, report.ref_code);
            name = name.replace(/\.[^.]+$/, '') + '-wm.png';
          }
          folder.file(name, blob);
        } catch (e) { /* ignore single failure */ }
      }));
      // Include a short README with metadata
      const meta = `Denuncia: ${report.ref_code}
Fecha: ${new Date(report.created_at).toLocaleString('es-MX')}
Estado: ${report.status}
Ubicación: ${[report.street, report.municipality, report.state, report.country].filter(Boolean).join(', ')}
Coordenadas: ${report.lat ?? '-'}, ${report.lng ?? '-'}
Contacto: ${report.contact || 'Anónimo'}
Marca de agua aplicada: ${watermarkEnabled ? 'SÍ (imágenes)' : 'NO'}

Descripción:
${report.description}

— RP NOTICIAS · La crítica de los hechos
Material recibido como evidencia ciudadana. Uso periodístico con atribución.
`;
      folder.file('INFO.txt', meta);
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `denuncia-${report.ref_code}.zip`);
      toast.success(`ZIP descargado (${allPaths.length} archivos)`);
    } catch (e) {
      console.error(e);
      toast.error('Error al generar el ZIP');
    } finally { setDownloading(false); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="h-7 w-7 animate-spin text-[#E50B15]" /></div>;

  const chartData = analytics?.daily?.map(d => ({ ...d, label: d.date.slice(5) })) || [];
  const pendingReports = reports.filter(r => r.status === 'pending').length;
  const lastSeen = parseInt(localStorage.getItem('rp_last_seen_reports') || '0', 10);
  const newReportsCount = reports.filter(r => new Date(r.created_at).getTime() > lastSeen).length;

  // Reusable report detail panel (used in both list and map views)
  const reportDetailPanel = !selectedReport ? (
    <div className="p-8 text-center">
      <Megaphone className="h-10 w-10 text-[#0B1F40]/10 mx-auto mb-3" />
      <p className="text-xs text-[#0B1F40]/40 font-bold">Selecciona una denuncia para ver detalles</p>
    </div>
  ) : (
    <div data-testid="report-detail">
      <div className="bg-[#E50B15] px-4 py-2 flex items-center justify-between">
        <h3 className="text-[10px] font-black tracking-[0.25em] uppercase text-white">DENUNCIA {selectedReport.ref_code}</h3>
        <button onClick={() => setSelectedReport(null)} className="text-white/70 hover:text-white text-[11px] font-black">×</button>
      </div>
      <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
        {/* Download all ZIP */}
        {(selectedReport.images?.length > 0 || selectedReport.videos?.length > 0) && (
          <div className="space-y-2">
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input type="checkbox" data-testid="watermark-toggle" checked={watermarkEnabled} onChange={(e) => setWatermarkEnabled(e.target.checked)}
                className="h-3.5 w-3.5 accent-[#E50B15]" />
              <span className="text-[9px] font-black text-[#0B1F40] tracking-[0.15em] uppercase">Aplicar marca de agua a imágenes</span>
            </label>
            <button data-testid="download-all-media" onClick={() => downloadAll(selectedReport)} disabled={downloading}
              className="w-full bg-[#0B1F40] text-white text-[10px] font-black tracking-[0.2em] uppercase h-10 flex items-center justify-center gap-2 hover:bg-[#E50B15] transition-colors disabled:opacity-50"
              style={{ boxShadow: '3px 3px 0 #E50B15' }}>
              {downloading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Package className="h-3 w-3" />}
              DESCARGAR TODO ({(selectedReport.images?.length || 0) + (selectedReport.videos?.length || 0)} ARCHIVOS .ZIP)
            </button>
          </div>
        )}
        <div>
          <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase mb-1">FECHA DE ENVÍO</p>
          <p className="text-xs font-bold text-[#0B1F40]">{new Date(selectedReport.created_at).toLocaleString('es-MX')}</p>
        </div>
        <div>
          <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase mb-1">UBICACIÓN</p>
          <p className="text-xs text-[#0B1F40] font-bold leading-relaxed">
            {[selectedReport.street, selectedReport.municipality, selectedReport.state, selectedReport.country].filter(Boolean).join(' · ') || '—'}
          </p>
          {typeof selectedReport.lat === 'number' && (
            <p className="text-[9px] text-[#0B1F40]/40 font-bold mt-1">📍 {selectedReport.lat.toFixed(4)}, {selectedReport.lng.toFixed(4)}</p>
          )}
        </div>
        <div>
          <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase mb-1">DESCRIPCIÓN</p>
          <p className="text-xs text-[#0B1F40] leading-relaxed whitespace-pre-wrap">{selectedReport.description}</p>
        </div>
        {selectedReport.contact && (
          <div>
            <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase mb-1">CONTACTO (OPCIONAL)</p>
            <p className="text-xs font-bold text-[#E50B15] break-all">{selectedReport.contact}</p>
          </div>
        )}
        {selectedReport.images?.length > 0 && (
          <div>
            <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase mb-2">IMÁGENES ({selectedReport.images.length})</p>
            <div className="grid grid-cols-2 gap-2">
              {selectedReport.images.map(p => (
                <div key={p} className="relative group">
                  <a href={getFileUrl(p)} target="_blank" rel="noreferrer" className="block aspect-square border border-[#0B1F40]/15 overflow-hidden bg-[#F4F5F7]">
                    <img src={getFileUrl(p)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </a>
                  <button data-testid={`download-img-${p.split('/').pop()}`} onClick={() => downloadOne(p, selectedReport.ref_code)}
                    className="absolute bottom-1 right-1 bg-[#E50B15] text-white h-7 w-7 flex items-center justify-center hover:bg-[#0B1F40] transition-colors opacity-0 group-hover:opacity-100"
                    title="Descargar">
                    <Download className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {selectedReport.videos?.length > 0 && (
          <div>
            <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase mb-2">VIDEOS ({selectedReport.videos.length})</p>
            <div className="space-y-2">
              {selectedReport.videos.map(p => (
                <div key={p} className="space-y-1">
                  <video src={getFileUrl(p)} controls className="w-full border border-[#0B1F40]/15" />
                  <button data-testid={`download-vid-${p.split('/').pop()}`} onClick={() => downloadOne(p, selectedReport.ref_code)}
                    className="w-full bg-[#0B1F40] text-white text-[9px] font-black tracking-[0.15em] uppercase h-8 flex items-center justify-center gap-1.5 hover:bg-[#E50B15] transition-colors">
                    <Download className="h-3 w-3" /> DESCARGAR VIDEO
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="pt-3 border-t-2 border-[#0B1F40]/10 space-y-2">
          <p className="text-[8px] text-[#0B1F40]/40 font-black tracking-[0.2em] uppercase">ACCIONES</p>
          <div className="grid grid-cols-2 gap-2">
            <button data-testid="mark-reviewed" onClick={() => setReportStatus(selectedReport.id, 'reviewed')} disabled={selectedReport.status === 'reviewed'}
              className="bg-emerald-600 text-white text-[10px] font-black h-9 flex items-center justify-center gap-1.5 hover:bg-emerald-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <CheckCircle className="h-3 w-3" /> REVISADA
            </button>
            <button data-testid="mark-archived" onClick={() => setReportStatus(selectedReport.id, 'archived')} disabled={selectedReport.status === 'archived'}
              className="bg-[#0B1F40] text-white text-[10px] font-black h-9 flex items-center justify-center gap-1.5 hover:bg-[#0B1F40]/80 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <Archive className="h-3 w-3" /> ARCHIVAR
            </button>
            <button data-testid="mark-pending" onClick={() => setReportStatus(selectedReport.id, 'pending')} disabled={selectedReport.status === 'pending'}
              className="bg-amber-500 text-white text-[10px] font-black h-9 flex items-center justify-center gap-1.5 hover:bg-amber-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
              <RefreshCw className="h-3 w-3" /> PENDIENTE
            </button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button data-testid="delete-report" className="bg-red-50 border-2 border-[#E50B15] text-[#E50B15] text-[10px] font-black h-9 flex items-center justify-center gap-1.5 hover:bg-[#E50B15] hover:text-white transition-colors">
                  <Trash2 className="h-3 w-3" /> ELIMINAR
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent className="rounded-none border-2 border-[#0B1F40] brutal-shadow">
                <AlertDialogHeader>
                  <AlertDialogTitle style={{ fontFamily: 'Archivo Black' }}>ELIMINAR DENUNCIA</AlertDialogTitle>
                  <AlertDialogDescription className="text-xs">Se eliminará la denuncia <strong>{selectedReport.ref_code}</strong> permanentemente.</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-none border-2 border-[#0B1F40]/20 font-black text-xs">CANCELAR</AlertDialogCancel>
                  <AlertDialogAction data-testid="confirm-delete-report" onClick={() => removeReport(selectedReport.id)} className="rounded-none bg-[#E50B15] text-white hover:bg-[#0B1F40] font-black text-xs">SÍ, ELIMINAR</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="mb-8 pb-4 border-b-4 border-[#0B1F40]">
        <span className="text-[10px] font-black tracking-[0.3em] uppercase text-[#E50B15]">PANEL DE CONTROL</span>
        <h1 className="text-3xl sm:text-4xl tracking-tighter text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>ADMINISTRACIÓN</h1>
      </div>

      {/* Stat Boxes */}
      {stats && (
        <div data-testid="admin-stats" className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { icon: <Newspaper className="h-4 w-4" />, label: 'NOTICIAS', val: stats.total_news, color: '' },
            { icon: <Clock className="h-4 w-4" />, label: 'PENDIENTES', val: stats.pending, color: 'border-l-amber-500 bg-amber-50' },
            { icon: <CheckCircle className="h-4 w-4" />, label: 'APROBADAS', val: stats.approved, color: 'border-l-emerald-600 bg-emerald-50' },
            { icon: <Users className="h-4 w-4" />, label: 'USUARIOS', val: stats.total_users, color: '' },
            { icon: <Eye className="h-4 w-4" />, label: 'VISITAS HOY', val: analytics?.today_views || 0, color: 'border-l-[#E50B15] bg-red-50' },
          ].map((s, i) => (
            <div key={i} className={`border-2 border-[#0B1F40]/10 border-l-4 p-4 ${s.color}`}>
              <div className="flex items-center gap-1.5 mb-1 text-[#0B1F40]/40">{s.icon}<span className="text-[9px] font-black tracking-[0.15em] uppercase">{s.label}</span></div>
              <p className="text-3xl font-black text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>{s.val}</p>
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="rounded-none border-2 border-[#0B1F40] bg-white p-0 h-auto flex flex-wrap">
          {[
            { v: 'analytics', l: 'Estadísticas', icon: <BarChart3 className="h-3 w-3" /> },
            { v: 'pending', l: `Pendientes (${pending.length})`, icon: <Clock className="h-3 w-3" /> },
            { v: 'users', l: `Usuarios (${usersData.length})`, icon: <UserCheck className="h-3 w-3" /> },
            { v: 'reports', l: `Denuncias (${pendingReports})`, icon: <Megaphone className="h-3 w-3" />, badge: newReportsCount > 0 && tab !== 'reports' ? newReportsCount : 0 },
            { v: 'categories', l: 'Categorías', icon: <TrendingUp className="h-3 w-3" /> },
          ].map(t => (
            <TabsTrigger key={t.v} data-testid={`tab-${t.v}`} value={t.v}
              className="rounded-none text-[10px] font-black tracking-wider data-[state=active]:bg-[#0B1F40] data-[state=active]:text-white text-[#0B1F40]/40 px-5 py-2.5 flex items-center gap-1.5 border-r border-[#0B1F40]/10 relative">
              {t.icon}{t.l}
              {t.badge > 0 && <span data-testid={`tab-badge-${t.v}`} className="absolute -top-1 -right-1 bg-[#E50B15] text-white text-[8px] font-black h-4 min-w-[16px] px-1 rounded-full flex items-center justify-center animate-pulse">{t.badge}</span>}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-6">
          {analytics && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="border-2 border-[#0B1F40] p-5 brutal-shadow">
                  <p className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/40 mb-1">TOTAL VISITAS</p>
                  <p className="text-4xl font-black text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>{analytics.total_views}</p>
                </div>
                <div className="border-2 border-[#E50B15] p-5 brutal-shadow-red">
                  <p className="text-[10px] font-black tracking-[0.15em] uppercase text-[#E50B15] mb-1">VISITAS HOY</p>
                  <p className="text-4xl font-black text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>{analytics.today_views}</p>
                </div>
                <div className="border-2 border-[#0B1F40] p-5 brutal-shadow">
                  <p className="text-[10px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/40 mb-1">VISITANTES ÚNICOS HOY</p>
                  <p className="text-4xl font-black text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>{analytics.today_unique}</p>
                </div>
              </div>

              {/* Chart - Views */}
              <div className="border-2 border-[#0B1F40] p-6 brutal-shadow">
                <h3 className="text-sm font-black tracking-wider uppercase text-[#0B1F40] mb-4" style={{ fontFamily: 'Archivo Black' }}>VISITAS ÚLTIMOS 30 DÍAS</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <AreaChart data={chartData}>
                    <CartesianGrid strokeDasharray="0" stroke="rgba(11,31,64,0.08)" />
                    <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#0B1F40', fontWeight: 700 }} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 9, fill: '#0B1F40' }} />
                    <Tooltip contentStyle={{ border: '2px solid #0B1F40', borderRadius: 0, fontFamily: 'Archivo Black', fontSize: 11 }} />
                    <Area type="stepAfter" dataKey="views" stroke="#E50B15" fill="#E50B15" fillOpacity={0.15} strokeWidth={2} name="Visitas" />
                    <Area type="stepAfter" dataKey="unique" stroke="#0B1F40" fill="#0B1F40" fillOpacity={0.08} strokeWidth={2} name="Únicos" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Top Pages */}
              {analytics.top_pages?.length > 0 && (
                <div className="border-2 border-[#0B1F40] brutal-shadow">
                  <div className="bg-[#0B1F40] text-white px-4 py-2">
                    <h3 className="text-[10px] font-black tracking-[0.2em] uppercase">PÁGINAS MÁS VISITADAS</h3>
                  </div>
                  <div className="divide-y divide-[#0B1F40]/10">
                    {analytics.top_pages.map((p, i) => (
                      <div key={i} className="px-4 py-2.5 flex items-center justify-between">
                        <span className="text-xs font-bold text-[#0B1F40] truncate">{p.page}</span>
                        <span className="text-xs font-black text-[#E50B15] ml-2">{p.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Auto-news panel */}
          <div className="mt-6 border-2 border-[#0B1F40] brutal-shadow overflow-hidden">
            <div className="bg-gradient-to-r from-[#0B1F40] to-[#E50B15] px-4 py-2 flex items-center justify-between">
              <h3 className="text-[10px] font-black tracking-[0.25em] uppercase text-white flex items-center gap-1.5"><Sparkles className="h-3 w-3" /> GENERADOR AUTOMÁTICO DE NOTICIAS (IA)</h3>
              <span className="text-[9px] font-bold text-white/70 tracking-wider">DIARIO 07:00 · MÉXICO</span>
            </div>
            <div className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-center">
              <div>
                <p className="text-xs text-[#0B1F40] font-bold mb-1">Genera 3 notas diarias sobre Tlaxcala usando Claude Sonnet 4.5.</p>
                <div className="flex items-center gap-4 mt-2 flex-wrap">
                  <div><p className="text-[9px] font-black tracking-[0.2em] uppercase text-[#0B1F40]/40">TOTAL AUTO-GENERADAS</p>
                    <p className="text-xl font-black text-[#E50B15]" style={{ fontFamily: 'Archivo Black' }}>{autoNewsStatus?.total_auto_generated ?? '—'}</p></div>
                  <div><p className="text-[9px] font-black tracking-[0.2em] uppercase text-[#0B1F40]/40">ÚLTIMA CORRIDA</p>
                    <p className="text-xs font-black text-[#0B1F40]">
                      {autoNewsStatus?.recent_runs?.[0]
                        ? `${autoNewsStatus.recent_runs[0].date} · ${autoNewsStatus.recent_runs[0].inserted ?? 0} notas${autoNewsStatus.recent_runs[0].completed ? '' : ' (en curso)'}`
                        : 'Sin corridas'}
                    </p></div>
                </div>
              </div>
              <button data-testid="run-auto-news" onClick={runAutoNewsNow} disabled={autoNewsRunning}
                className="bg-[#E50B15] text-white font-black text-[10px] tracking-[0.2em] uppercase h-11 px-5 flex items-center justify-center gap-2 brutal-btn-press transition-all disabled:opacity-60"
                style={{ boxShadow: '3px 3px 0 #0B1F40' }}>
                {autoNewsRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                GENERAR AHORA
              </button>
            </div>
            {autoNewsStatus?.recent_runs?.length > 1 && (
              <details className="border-t-2 border-[#0B1F40]/10 p-3">
                <summary className="text-[9px] font-black tracking-[0.2em] uppercase text-[#0B1F40]/50 cursor-pointer">HISTORIAL</summary>
                <div className="mt-2 space-y-1">
                  {autoNewsStatus.recent_runs.slice(0, 10).map(r => (
                    <div key={r.date} className="flex items-center justify-between text-[10px] text-[#0B1F40]/60 font-bold border-b border-[#0B1F40]/5 py-1">
                      <span>{r.date}</span>
                      <span>{r.inserted ?? 0} ins · {r.errors ?? 0} err {r.completed ? '✓' : '…'}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        </TabsContent>

        {/* Pending Tab */}
        <TabsContent value="pending" className="mt-4">
          {pending.length === 0 ? (
            <div className="text-center py-16 border-2 border-dashed border-[#0B1F40]/15">
              <CheckCircle className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
              <p className="text-sm text-[#0B1F40]/40 font-bold">No hay pendientes</p>
            </div>
          ) : (
            <div className="space-y-2">{pending.map(n => <NewsCardAdmin key={n.id} news={n} onApprove={approve} onReject={reject} />)}</div>
          )}
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="mt-4">
          <div className="border-2 border-[#0B1F40] brutal-shadow overflow-hidden">
            <div className="bg-[#0B1F40] text-white px-4 py-2 flex items-center justify-between">
              <h3 className="text-[10px] font-black tracking-[0.2em] uppercase">REGISTRO DE USUARIOS</h3>
              <span className="text-[10px] font-bold text-white/60">{usersData.length} usuarios</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b-2 border-[#0B1F40] bg-[#F4F5F7]">
                    <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">FOTO</th>
                    <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">NOMBRE</th>
                    <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">EMAIL</th>
                    <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">ID</th>
                    <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">ROL</th>
                    <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">TEL</th>
                    <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">NOTICIAS</th>
                    <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">REGISTRO</th>
                    <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">ACCIONES</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#0B1F40]/8">
                  {usersData.map(u => (
                    <tr key={u.id} data-testid={`user-row-${u.email}`} className="hover:bg-[#F4F5F7] transition-colors">
                      <td className="px-3 py-2">
                        <div className="w-8 h-8 border border-[#0B1F40]/15 bg-[#F4F5F7] overflow-hidden flex items-center justify-center">
                          {u.photo_url ? <img src={u.photo_url.startsWith('http') ? u.photo_url : `/api/files/${u.photo_url}`} alt="" className="w-full h-full object-cover" /> :
                            <span className="text-[10px] font-black text-[#0B1F40]/20">{u.name?.charAt(0)}</span>}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs font-bold text-[#0B1F40]">{u.name}</td>
                      <td className="px-3 py-2 text-[10px] text-[#0B1F40]/60">{u.email}</td>
                      <td className="px-3 py-2 text-[10px] font-black text-[#E50B15]">{u.user_id_code || '-'}</td>
                      <td className="px-3 py-2">
                        <span className={`text-[8px] font-black tracking-wider uppercase px-2 py-0.5 ${u.role === 'admin' ? 'bg-[#E50B15] text-white' : 'bg-[#0B1F40]/5 text-[#0B1F40]/50'}`}>{u.role}</span>
                      </td>
                      <td className="px-3 py-2 text-[10px] text-[#0B1F40]/50">{u.phone || '-'}</td>
                      <td className="px-3 py-2 text-xs font-black text-[#0B1F40]">{u.news_count}</td>
                      <td className="px-3 py-2 text-[10px] text-[#0B1F40]/40">{u.created_at ? new Date(u.created_at).toLocaleDateString('es-ES') : '-'}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <Link to={`/credential/${u.id}`}>
                            <button data-testid={`credential-btn-${u.id}`} className="border border-[#0B1F40]/15 h-7 px-2 flex items-center gap-1 text-[9px] font-black text-[#0B1F40] hover:border-[#E50B15] hover:text-[#E50B15] transition-colors">
                              <CreditCard className="h-3 w-3" /> CREDENCIAL
                            </button>
                          </Link>
                          {u.role !== 'admin' && (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <button data-testid={`delete-user-${u.id}`} title="Eliminar usuario"
                                  className="border border-[#E50B15]/30 h-7 w-7 flex items-center justify-center text-[#E50B15] hover:bg-[#E50B15] hover:text-white transition-colors">
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </AlertDialogTrigger>
                              <AlertDialogContent className="rounded-none border-2 border-[#0B1F40] brutal-shadow">
                                <AlertDialogHeader>
                                  <AlertDialogTitle className="text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>ELIMINAR USUARIO</AlertDialogTitle>
                                  <AlertDialogDescription className="text-xs text-[#0B1F40]/60">
                                    Vas a eliminar permanentemente a <strong>{u.name}</strong> ({u.email}) y <strong>todas sus noticias</strong>. Esta acción no se puede deshacer.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel className="rounded-none border-2 border-[#0B1F40]/20 font-black text-xs">CANCELAR</AlertDialogCancel>
                                  <AlertDialogAction data-testid={`confirm-delete-user-${u.id}`} onClick={() => removeUser(u.id, u.name)}
                                    className="rounded-none bg-[#E50B15] text-white hover:bg-[#0B1F40] font-black text-xs">SÍ, ELIMINAR</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-4">
          {/* View toggle */}
          <div className="flex items-center gap-2 mb-4">
            <button data-testid="reports-view-list" onClick={() => setReportsView('list')}
              className={`h-9 px-4 text-[10px] font-black tracking-[0.2em] uppercase flex items-center gap-1.5 border-2 transition-all ${reportsView === 'list' ? 'bg-[#0B1F40] text-white border-[#0B1F40]' : 'bg-white text-[#0B1F40]/60 border-[#0B1F40]/20 hover:border-[#0B1F40]'}`}>
              <List className="h-3 w-3" /> LISTA
            </button>
            <button data-testid="reports-view-map" onClick={() => setReportsView('map')}
              className={`h-9 px-4 text-[10px] font-black tracking-[0.2em] uppercase flex items-center gap-1.5 border-2 transition-all ${reportsView === 'map' ? 'bg-[#E50B15] text-white border-[#E50B15]' : 'bg-white text-[#0B1F40]/60 border-[#0B1F40]/20 hover:border-[#E50B15]'}`}>
              <MapIcon className="h-3 w-3" /> MAPA
            </button>
            <span className="text-[10px] font-bold text-[#0B1F40]/40 ml-2">
              {reports.filter(r => typeof r.lat === 'number').length} / {reports.length} geolocalizadas
            </span>
          </div>

          {reportsView === 'map' ? (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
              <ReportsMap reports={reports} onSelect={setSelectedReport} selectedId={selectedReport?.id} />
              <aside className="border-2 border-[#0B1F40] brutal-shadow overflow-hidden h-fit lg:sticky lg:top-24">
                {reportDetailPanel}
              </aside>
            </div>
          ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-4">
            {/* Table */}
            <div className="border-2 border-[#0B1F40] brutal-shadow overflow-hidden">
              <div className="bg-[#0B1F40] text-white px-4 py-2 flex items-center justify-between">
                <h3 className="text-[10px] font-black tracking-[0.2em] uppercase flex items-center gap-2"><Megaphone className="h-3 w-3" /> DENUNCIAS ANÓNIMAS</h3>
                <span className="text-[10px] font-bold text-white/60">{reports.length} totales · {pendingReports} pendientes</span>
              </div>
              {reports.length === 0 ? (
                <div className="p-10 text-center">
                  <Megaphone className="h-8 w-8 text-[#0B1F40]/20 mx-auto mb-2" />
                  <p className="text-[#0B1F40]/40 font-bold text-xs">No hay denuncias</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b-2 border-[#0B1F40] bg-[#F4F5F7]">
                        <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">FOLIO</th>
                        <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">FECHA</th>
                        <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">UBICACIÓN</th>
                        <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">DESCRIPCIÓN</th>
                        <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">MEDIOS</th>
                        <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">ESTADO</th>
                        <th className="px-3 py-2 text-[9px] font-black tracking-[0.15em] uppercase text-[#0B1F40]/60">ACCIONES</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#0B1F40]/8">
                      {reports.map(r => (
                        <tr key={r.id} data-testid={`report-row-${r.id}`} className={`hover:bg-[#F4F5F7] transition-colors ${selectedReport?.id === r.id ? 'bg-[#F4F5F7]' : ''}`}>
                          <td className="px-3 py-2 text-[10px] font-black text-[#E50B15]">{r.ref_code}</td>
                          <td className="px-3 py-2 text-[10px] text-[#0B1F40]/50">{new Date(r.created_at).toLocaleDateString('es-MX')}</td>
                          <td className="px-3 py-2 text-[10px] text-[#0B1F40]/70 max-w-[180px] truncate">
                            <span className="inline-flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />{[r.municipality, r.state].filter(Boolean).join(', ') || '-'}</span>
                          </td>
                          <td className="px-3 py-2 text-[10px] text-[#0B1F40] max-w-[260px] truncate font-bold">{r.description}</td>
                          <td className="px-3 py-2 text-[10px] text-[#0B1F40]/60">
                            <span className="inline-flex items-center gap-1 mr-2"><ImageIcon className="h-2.5 w-2.5" />{r.images?.length || 0}</span>
                            <span className="inline-flex items-center gap-1"><VideoIcon className="h-2.5 w-2.5" />{r.videos?.length || 0}</span>
                          </td>
                          <td className="px-3 py-2">
                            <span className={`text-[8px] font-black tracking-wider uppercase px-2 py-0.5 ${r.status === 'pending' ? 'bg-amber-100 text-amber-800' : r.status === 'reviewed' ? 'bg-emerald-100 text-emerald-800' : 'bg-[#0B1F40]/5 text-[#0B1F40]/50'}`}>
                              {r.status === 'pending' ? 'PENDIENTE' : r.status === 'reviewed' ? 'REVISADA' : 'ARCHIVADA'}
                            </span>
                          </td>
                          <td className="px-3 py-2">
                            <button data-testid={`view-report-${r.id}`} onClick={() => setSelectedReport(r)}
                              className="border border-[#0B1F40]/15 h-7 px-2 text-[9px] font-black text-[#0B1F40] hover:border-[#E50B15] hover:text-[#E50B15] transition-colors">
                              VER
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Detail panel */}
            <aside className="border-2 border-[#0B1F40] brutal-shadow overflow-hidden h-fit lg:sticky lg:top-24">
              {reportDetailPanel}
            </aside>
          </div>
          )}
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories" className="mt-4">
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {stats.categories.map(c => (
                <div key={c.category} data-testid={`stat-${c.category.toLowerCase()}`}
                  className="border-2 border-[#0B1F40]/10 p-4 hover:border-[#0B1F40] brutal-shadow-hover brutal-btn-press transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-black text-[#0B1F40]" style={{ fontFamily: 'Archivo Black' }}>{c.category}</span>
                    <span className="text-xs font-black text-[#E50B15]">{c.count}</span>
                  </div>
                  <div className="h-2 bg-[#0B1F40]/5"><div className="h-full bg-[#E50B15]" style={{ width: `${stats.approved > 0 ? (c.count / stats.approved) * 100 : 0}%` }} /></div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
